import { useEffect, useState } from 'react'
import { Building2, Globe, User } from 'lucide-react'
import { Input } from '@/components/ui/Input'
import { CompanySearchSelect, type V2EmpresaOption } from '@/components/CloserLeads/CompanySearchSelect'
import { formatPhone } from '@/utils/formatPhone'
import { fetchEmpresaRegistrationPrefill } from '@/utils/leadRegistrationValidation'
import { toast } from 'sonner'
import api from '@/services/api'
import type { CloserLeadFormData } from '@/types'

interface EmpresaSectionProps {
  formData: CloserLeadFormData
  onFieldChange: (field: string, value: string) => void
  onMultiFieldChange?: (fields: Partial<CloserLeadFormData>) => void
  errors?: Record<string, string>
  highlightEmpresa?: boolean
  onEmpresaFilled?: () => void
}

export function EmpresaSection({
  formData,
  onFieldChange,
  onMultiFieldChange,
  errors,
  highlightEmpresa = false,
  onEmpresaFilled,
}: EmpresaSectionProps) {
  const [contatosEmpresa, setContatosEmpresa] = useState<any[]>([])

  useEffect(() => {
    if (!formData.empresaId) {
      setContatosEmpresa([])
      return
    }
    api
      .get(`/empresas/${formData.empresaId}`)
      .then(resp => {
        const empresa = resp.data?.data ?? resp.data
        setContatosEmpresa(empresa?.contatos || [])
      })
      .catch(() => setContatosEmpresa([]))
  }, [formData.empresaId])

  const handleCompanySelect = async (company: V2EmpresaOption) => {
    const base: Partial<CloserLeadFormData> = {
      empresaId: company.id,
      empresa: company.nome || '',
      site: company.site || formData.site || '',
      segmento: company.segmento || formData.segmento || '',
      plataforma: company.plataforma || formData.plataforma || '',
      faturamento: company.faturamento || formData.faturamento || '',
    }
    onMultiFieldChange?.(base)

    if (company.nome?.trim()) {
      onEmpresaFilled?.()
    }

    const prefill = await fetchEmpresaRegistrationPrefill(company.id)
    if (prefill) {
      onMultiFieldChange?.({
        ...base,
        contato: prefill.contato || formData.contato,
        cargo: prefill.cargo || formData.cargo,
        telefone: prefill.telefone || formData.telefone,
        email: prefill.email || formData.email,
        site: prefill.site || base.site,
        segmento: prefill.segmento || base.segmento,
        plataforma: prefill.plataforma || base.plataforma,
        modelo: prefill.modelo || formData.modelo,
        faturamento: prefill.faturamento || base.faturamento,
        trafego: prefill.trafego || formData.trafego,
        origem: prefill.origem || formData.origem,
        problema: prefill.problema || formData.problema,
        observacoes: prefill.observacoes || formData.observacoes,
        servicos: prefill.servicos?.length ? prefill.servicos : formData.servicos,
        score: prefill.score ?? formData.score,
        parceiroId: prefill.parceiroId ?? formData.parceiroId,
      })
      toast.info('Dados anteriores desta empresa foram carregados no formulário')
    }
  }

  const handleClearSelection = () => {
    onMultiFieldChange?.({ empresaId: undefined })
  }

  const handleSelectContact = (contact: any) => {
    onMultiFieldChange?.({
      contato: contact.nome || formData.contato,
      cargo: contact.cargo || formData.cargo || '',
      telefone: contact.telefone ? formatPhone(contact.telefone) : formData.telefone,
      email: contact.email || formData.email || '',
    })
  }

  const handleEmpresaChange = (value: string) => {
    onFieldChange('empresa', value)
    if (value.trim().length >= 2) {
      onEmpresaFilled?.()
    }
  }

  const showEmpresaPending = highlightEmpresa && !formData.empresa?.trim()

  return (
    <div className='space-y-4'>
      <div className='flex items-center gap-2 mb-4'>
        <div className='p-1.5 rounded-lg bg-accent/10 text-accent'>
          <Building2 size={16} />
        </div>
        <span className='text-xs font-bold uppercase tracking-[0.15em] text-text-muted'>
          Dados da Empresa
        </span>
      </div>

      {showEmpresaPending && (
        <div className='rounded-xl border border-amber-500/35 bg-amber-500/10 px-4 py-3 text-sm text-amber-900 dark:text-amber-200'>
          <p className='font-semibold'>Falta informar a empresa</p>
          <p className='mt-1 text-xs leading-relaxed text-amber-800/90 dark:text-amber-100/90'>
            O contato foi lido do QR. Digite ou busque o nome da empresa abaixo para cadastrar o lead.
          </p>
        </div>
      )}

      <CompanySearchSelect
        value={formData.empresa}
        onSelect={handleCompanySelect}
        onChange={handleEmpresaChange}
        onClearSelection={handleClearSelection}
        error={errors?.empresa}
        highlighted={showEmpresaPending}
        required
      />

      {contatosEmpresa.length > 0 && (
        <div className='pt-2 border-t border-[var(--glass-border)]'>
          <span className='text-[10px] text-[var(--text-muted)] font-bold block uppercase tracking-wider mb-2'>
            Contatos existentes na empresa
          </span>
          <div className='flex flex-wrap gap-2'>
            {contatosEmpresa.map((contact: any) => (
              <button
                key={contact.id}
                type='button'
                onClick={() => handleSelectContact(contact)}
                className='flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[var(--glass-bg)] border border-[var(--glass-border)] hover:border-accent/40 hover:bg-accent/5 transition-colors text-left'
              >
                <User size={12} className='text-accent shrink-0' />
                <span className='text-[11px] font-medium text-[var(--text-primary)]'>{contact.nome}</span>
                {contact.cargo && (
                  <span className='text-[10px] text-text-muted'>({contact.cargo})</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className='grid grid-cols-1 gap-4'>
        <Input
          label='Site'
          value={formData.site}
          onChange={e => onFieldChange('site', e.target.value)}
          placeholder='https://... (opcional)'
          iconRight={<Globe size={14} className='text-text-muted' />}
        />
      </div>
    </div>
  )
}

export default EmpresaSection
